import React, { useState, useEffect, useRef, useContext } from "react";
import dayjs from "dayjs";
import { WarningOutlined } from "@ant-design/icons";
import { DatePicker, Input, Checkbox, Table, Tooltip, Tag } from "antd";
import { FaRegNewspaper } from "react-icons/fa6";
import mainContext from "../context/MainContext/mainContext";
import isBetween from "dayjs/plugin/isBetween"; // Import isBetween plugin
import {
  monthToDurationRate,
  normalizeRateRecord,
} from "../../../../../utils/bdfBillingRateCalc";
import { findBilledLineForRecord } from "../../../../../utils/billingInvMatch";
import "./MagazineIndicator.css";
dayjs.extend(isBetween);

const MAGAZINE_MEDIA_TYPE_ID = "66ab804cd0da793340aca037";

const isMagazineInventory = (record) =>
  String(record?.mediaTypeId || "") === MAGAZINE_MEDIA_TYPE_ID;

/** When Total (BDF line) and Due (API) differ, skip auto-prefill so the user picks Billing Period manually. */
function totalAndDueDifferForAutoPeriod(inv) {
  const rawDue = inv?.apiDueAmount;
  if (rawDue === undefined || rawDue === null || rawDue === "") return false;
  const total = Number(inv?.originalSpecialRate) || 0;
  const due = Number(rawDue);
  return Math.round(total) !== Math.round(due);
}

/** Billing (specialRate) must be <= API due when due exists. */
function billingAmountExceedsDueForRow(record) {
  const rawDue = record?.apiDueAmount;
  if (rawDue === undefined || rawDue === null || rawDue === "") return false;
  const due = Number(rawDue);
  if (!Number.isFinite(due)) return false;
  const billing = Number(record?.specialRate) || 0;
  return Math.round(billing) > Math.round(due);
}

/**
 * Do not pass `defaultPickerValue` to RangePicker: rc-picker resyncs it every time the active
 * field switches (start → end), which jumps the calendar back to the booking window month even
 * after the user navigates to another month (e.g. March). We keep the same open-month UX by
 * setting controlled `pickerValue` in `onOpenChange` instead. `pickerValue` must always be a
 * full [start, end] tuple with valid dayjs instances (date-only picker; no time fill issues).
 */
function BillingPeriodRangePicker({
  record,
  billingData,
  rangeValue,
  defaultPickerValue,
  getDisabledDates,
  onRangeChange,
}) {
  const [pickerPanelValue, setPickerPanelValue] = useState(null);

  const resolveInitialPicker = () => {
    if (
      rangeValue?.[0]?.isValid() &&
      rangeValue?.[1]?.isValid()
    ) {
      const m = rangeValue[0].startOf("month");
      return [m, m];
    }
    if (defaultPickerValue?.[0]?.isValid()) {
      return [defaultPickerValue[0], defaultPickerValue[0]];
    }
    return null;
  };

  return (
    <DatePicker.RangePicker
      value={rangeValue}
      pickerValue={pickerPanelValue ?? undefined}
      onPickerValueChange={(dates) => {
        if (dates?.[0] && dates?.[1]) {
          setPickerPanelValue(dates);
        }
      }}
      onCalendarChange={(dates, _dateStrings, info) => {
        if (info?.range === "start" && dates?.[0]) {
          const m = dates[0].startOf("month");
          setPickerPanelValue([m, m]);
        }
      }}
      onOpenChange={(open) => {
        if (open) {
          setPickerPanelValue(resolveInitialPicker());
        }
      }}
      onChange={(dates, dateStrings) => {
        onRangeChange(dates, dateStrings);
      }}
      format={"DD-MM-YYYY"}
      style={{ width: "230px" }}
      disabledDate={getDisabledDates(record, billingData)}
      allowClear={false}
    />
  );
}

const InventoryList = ({
  billingData,
  handleInvChange,
  setBillingData,
  handleFormChange,
  log,
  handleSelectionChange,
}) => {
  // let { archivedInvoiceData } = useContext(mainContext);
  // State to track selected rows
  const [selectedRows, setSelectedRows] = useState({});
  const [baseRates, setBaseRates] = useState({});
  const initializedRef = useRef(false);
  const archivedDataProcessedRef = useRef(false);

  useEffect(() => {
    if (billingData?.inventories && billingData?.billedInvs) {
      setBaseRates((prevBaseRates) => {
        const updatedBaseRates = { ...prevBaseRates };

        billingData.inventories.forEach((item) => {
          if (!updatedBaseRates[item._id]) {
            updatedBaseRates[item._id] = item.specialRate;
          }
        });

        return updatedBaseRates;
      });
    }
  }, [billingData?.inventories, billingData?.billedInvs]);

  useEffect(() => {
    if (billingData?.inventories) {
      setSelectedRows((prevSelectedRows) => {
        const updatedSelection = { ...prevSelectedRows };

        // billingData.inventories.forEach((item) => {
        //   // ✅ Preserve previous selection, only add new ones
        //   if (!(item._id in updatedSelection)) {
        //     updatedSelection[item._id] = true; // Default select new entries only
        //   }
        // });

        billingData.inventories.forEach((item) => {
          // Check if inventory is billed
          const inventoryBilled = isInventoryBilled(item, billingData);

          if (inventoryBilled) {
            delete updatedSelection[item._id]; // Remove from selection
          } else if (!(item._id in updatedSelection)) {
            updatedSelection[item._id] = true; // Default select new entries
          }
        });

        // ✅ Update selected inventories immediately
        handleSelectionChange(
          billingData.inventories.filter(
            (item) =>
              updatedSelection[item._id] &&
              !isInventoryBilled(item, billingData)
          )
        );

        return updatedSelection;
      });
    }
  }, [billingData?.inventories]);

  // useEffect(() => {
  //   if (
  //     archivedInvoiceData?.billedInvs?.length > 0 &&
  //     billingData?.inventories?.length > 0 &&
  //     !archivedDataProcessedRef.current
  //   ) {
  //     updateBillingDataFromArchived();
  //     archivedDataProcessedRef.current = true;
  //   }
  // }, [archivedInvoiceData]);

  // useEffect(() => {
  //   if (!archivedInvoiceData || !archivedInvoiceData?.billedInvs?.length) {
  //     archivedDataProcessedRef.current = false;
  //   }
  // }, [archivedInvoiceData]);

  // Function to update billing data from archived invoice data
  // const updateBillingDataFromArchived = () => {
  //   if (!archivedInvoiceData?.billedInvs || !billingData?.inventories) return;

  //   const updatedInventories = billingData.inventories.map((inv) => {
  //     // Find matching archived invoice data by comparing CodeNo or locationName
  //     const archivedInv = archivedInvoiceData.billedInvs.find(
  //       (archived) =>
  //         (archived.CodeNo && inv.CodeNo && archived.CodeNo === inv.CodeNo) ||
  //         (archived.locationName &&
  //           inv.locationName &&
  //           archived.locationName === inv.locationName)
  //     );

  //     if (archivedInv) {
  //       return {
  //         ...inv,
  //         // Update specialRate from archived data's amount
  //         specialRate: archivedInv.amount || inv.specialRate,
  //         // Update billing period from archived data
  //         billingStartDate: archivedInv.startDate
  //           ? new Date(archivedInv.startDate).toISOString().split("T")[0]
  //           : inv.billingStartDate,
  //         billingEndDate: archivedInv.endDate
  //           ? new Date(archivedInv.endDate).toISOString().split("T")[0]
  //           : inv.billingEndDate,
  //       };
  //     }

  //     return inv;
  //   });

  //   // Update the billing data with the values from archived invoice
  //   setBillingData((prev) => ({
  //     ...prev,
  //     inventories: updatedInventories,
  //   }));
  // };

  // Separate useEffect for initialization - runs only once when data is ready
  useEffect(() => {
    // Only run if we have inventories and base rates, and haven't initialized yet
    if (
      billingData?.inventories?.length > 0 &&
      Object.keys(baseRates).length > 0 &&
      !initializedRef.current
    ) {
      initializeAvailableBillingPeriods();
      initializedRef.current = true; // Mark as initialized to prevent infinite loops
    }
  }, [billingData?.inventories, baseRates]);

  const isAllDatesBlocked = (record, billingData) => {
    const disabledFn = getDisabledDates(record, billingData);

    const bookingStart = record.startDate
      ? dayjs(record.startDate, "YYYY-MM-DD")
      : null;
    const bookingEnd = record.endDate
      ? dayjs(record.endDate, "YYYY-MM-DD")
      : null;

    if (!bookingStart || !bookingEnd) return true; // If no valid range, treat as blocked

    let allBlocked = true;
    let currentDate = bookingStart;

    while (
      currentDate.isBefore(bookingEnd) ||
      currentDate.isSame(bookingEnd, "day")
    ) {
      if (!disabledFn(currentDate)) {
        allBlocked = false;
        break;
      }
      currentDate = currentDate.add(1, "day");
    }

    return allBlocked;
  };

  // New function to check if inventory is billed
  const isInventoryBilled = (record, billingData) => {
    if (!billingData?.billedInvs) return false;

    const billedInv = findBilledLineForRecord(billingData.billedInvs, record);

    return billedInv?.isBilled === true;
  };

  // Function to get billed periods for an inventory
  const getBilledPeriods = (record, billingData) => {
    if (!billingData?.billedInvs) return [];

    const billedInv = findBilledLineForRecord(billingData.billedInvs, record);

    return billedInv?.billedPeriod || record?.billedPeriod || [];
  };

  // Function to format billed periods for tooltip
  const formatBilledPeriodsTooltip = (billedPeriods) => {
    return (
      <div style={{ minWidth: "200px" }}>
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "8px",
            color: "#1890ff",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "4px",
          }}
        >
          📋 Previously Billed Periods ({billedPeriods.length})
        </div>
        {billedPeriods.map((period, index) => {
          const startDate = dayjs(period.startDate);
          const endDate = dayjs(period.endDate);
          const duration = endDate.diff(startDate, "days") + 1;

          return (
            <div
              key={period._id || index}
              style={{
                marginBottom: "6px",
                padding: "4px 8px",
                backgroundColor: "#f6f6f6",
                borderRadius: "4px",
                border: "1px solid #e8e8e8",
              }}
            >
              <div
                style={{
                  color: "#1890ff",
                  fontWeight: "500",
                  fontSize: "13px",
                }}
              >
                {startDate.format("DD/MM/YYYY")} -{" "}
                {endDate.format("DD/MM/YYYY")}
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: "11px",
                  marginTop: "2px",
                }}
              >
                Duration: {duration} day{duration !== 1 ? "s" : ""}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /** Total ≠ Due: billing amount stays empty until Billing Period dates are chosen; then it follows rate math + edits. */
  const billingAmountNeedsDatesFirst = (record) =>
    totalAndDueDifferForAutoPeriod(record) &&
    (!record.billingStartDate || !record.billingEndDate);

  /** Numeric billing line amount for totals (0 when waiting for dates or unset). */
  const getBillingAmount = (record) => {
    if (billingAmountNeedsDatesFirst(record)) return 0;
    const s = record?.specialRate;
    if (s === null || s === undefined || s === "") return 0;
    return Number(s) || 0;
  };

  const getBillingAmountDisplay = (record) => {
    if (billingAmountNeedsDatesFirst(record)) return "";
    const s = record?.specialRate;
    if (s === null || s === undefined || s === "") return "";
    return String(Math.round(Number(s)));
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (recordId) => {
    setSelectedRows((prev) => {
      const updatedSelection = { ...prev, [recordId]: !prev[recordId] };
      handleSelectionChange(
        billingData.inventories.filter((item) => updatedSelection[item._id])
      ); // Pass only selected inventories
      return updatedSelection;
    });
  };

  const handleBillingPeriodChange = (dates, dateStrings, recordId) => {
    if (!dateStrings[0] || !dateStrings[1]) return;

    const billingStartDDMM = dateStrings[0];
    const billingEndDDMM = dateStrings[1];

    setBillingData((prev) => {
      const inv = prev.inventories.find((i) => i._id === recordId);
      if (!inv) return prev;

      const nextStart = dayjs(billingStartDDMM, "DD-MM-YYYY").format(
        "YYYY-MM-DD",
      );
      const nextEnd = dayjs(billingEndDDMM, "DD-MM-YYYY").format(
        "YYYY-MM-DD",
      );

      // Same rules as BDFCreatorContainer handleDateRangeChange (no package rate)
      const normalized = normalizeRateRecord(inv);
      const { newSpecialRate } = monthToDurationRate(
        normalized,
        billingStartDDMM,
        billingEndDDMM,
      );
      const newBillingAmount = newSpecialRate || 0;

      return {
        ...prev,
        inventories: prev.inventories.map((item) => {
          if (item._id !== recordId) return item;
          return {
            ...item,
            billingStartDate: nextStart,
            billingEndDate: nextEnd,
            specialRate: newBillingAmount,
            billingAmountMode: "period",
          };
        }),
      };
    });
  };

  // NEW FUNCTION: Initialize billing periods with first available dates
  const initializeAvailableBillingPeriods = () => {
    if (!billingData?.inventories) return;

    const shouldAutoPrefillRemainingPeriod = (inv) => {
      const rawDue = inv?.apiDueAmount;
      const due =
        rawDue === undefined || rawDue === null || rawDue === ""
          ? null
          : Number(rawDue);
      if (due === null || !Number.isFinite(due) || due <= 0) return false;

      const billedPeriods = getBilledPeriods(inv, billingData);
      return billedPeriods.length > 0 && (!inv.billingStartDate || !inv.billingEndDate);
    };

    const mergeBilledPeriods = (periods) => {
      const parsed = periods
        .map((p) => ({
          start: dayjs(p.startDate).startOf("day"),
          end: dayjs(p.endDate).startOf("day"),
        }))
        .filter((p) => p.start.isValid() && p.end.isValid())
        .sort((a, b) => a.start.valueOf() - b.start.valueOf());

      const merged = [];
      for (const p of parsed) {
        const last = merged[merged.length - 1];
        if (!last) {
          merged.push({ ...p });
          continue;
        }

        // Merge overlapping or touching ranges (end + 1 day >= next start)
        if (p.start.isSame(last.end.add(1, "day"), "day") || p.start.isBefore(last.end.add(1, "day"), "day")) {
          if (p.end.isAfter(last.end)) last.end = p.end;
        } else {
          merged.push({ ...p });
        }
      }
      return merged;
    };

    /**
     * Finds the first "remaining/unbilled" contiguous date range within the booking window.
     * IMPORTANT: We do NOT disable billed dates in the picker (user may bill same dates again),
     * but for convenience we still auto-prefill the first gap after previously billed periods.
     */
    const findFirstRemainingDateRange = (record) => {
      const bookingStart = record.startDate
        ? dayjs(record.startDate, "YYYY-MM-DD").startOf("day")
        : null;
      const bookingEnd = record.endDate
        ? dayjs(record.endDate, "YYYY-MM-DD").startOf("day")
        : null;
      if (!bookingStart || !bookingEnd || !bookingStart.isValid() || !bookingEnd.isValid()) return null;

      const billed = mergeBilledPeriods(getBilledPeriods(record, billingData));
      if (billed.length === 0) return null;

      // Clamp billed periods to booking window
      const clamped = billed
        .map((p) => ({
          start: p.start.isBefore(bookingStart) ? bookingStart : p.start,
          end: p.end.isAfter(bookingEnd) ? bookingEnd : p.end,
        }))
        .filter((p) => !p.end.isBefore(p.start));

      // Walk to find the first day not covered
      let cursor = bookingStart;
      for (const p of clamped) {
        if (cursor.isBefore(p.start, "day")) {
          // gap: [cursor, day before p.start]
          return { start: cursor, end: p.start.subtract(1, "day") };
        }
        if (cursor.isSame(p.start, "day") || cursor.isBetween(p.start, p.end, "day", "[]")) {
          cursor = p.end.add(1, "day");
          if (cursor.isAfter(bookingEnd, "day")) return null;
        }
      }

      // Remaining tail after last billed period
      if (cursor.isSame(bookingEnd, "day") || cursor.isBefore(bookingEnd, "day")) {
        return { start: cursor, end: bookingEnd };
      }
      return null;
    };

    const candidates = billingData.inventories
      .filter((inv) => !inv.billingStartDate || !inv.billingEndDate)
      .filter(
        (inv) =>
          !totalAndDueDifferForAutoPeriod(inv) ||
          shouldAutoPrefillRemainingPeriod(inv) ||
          Boolean(billingData?.splitAmount)
      )
      .map((inv) => {
        const availableDateRange = shouldAutoPrefillRemainingPeriod(inv)
          ? findFirstRemainingDateRange(inv)
          : findFirstAvailableDateRange(inv, billingData);
        if (!availableDateRange) return null;
        return { inv, availableDateRange };
      })
      .filter(Boolean);

    if (candidates.length === 0) return;

    const updates = {};
    candidates.forEach(({ inv, availableDateRange }) => {
      const { start, end } = availableDateRange;
      updates[inv._id] = {
        billingStartDate: start.format("YYYY-MM-DD"),
        billingEndDate: end.format("YYYY-MM-DD"),
      };
    });

    setBillingData((prev) => ({
      ...prev,
      inventories: prev.inventories.map((inv) =>
        updates[inv._id] ? { ...inv, ...updates[inv._id] } : inv,
      ),
    }));
  };

  // NEW FUNCTION: Find first available date range
  const findFirstAvailableDateRange = (record, billingData) => {
    const disabledDateFn = getDisabledDates(record, billingData);

    const bookingStart = record.startDate
      ? dayjs(record.startDate, "YYYY-MM-DD")
      : null;
    const bookingEnd = record.endDate
      ? dayjs(record.endDate, "YYYY-MM-DD")
      : null;

    if (!bookingStart || !bookingEnd) return null;

    let rangeStart = null;
    let rangeEnd = null;
    let currentDate = bookingStart;

    // Find first available start date
    while (
      currentDate.isBefore(bookingEnd) ||
      currentDate.isSame(bookingEnd, "day")
    ) {
      if (!disabledDateFn(currentDate)) {
        rangeStart = currentDate;
        break;
      }
      currentDate = currentDate.add(1, "day");
    }

    if (!rangeStart) return null; // No available start date found

    // Find continuous range end
    currentDate = rangeStart;
    while (
      currentDate.isBefore(bookingEnd) ||
      currentDate.isSame(bookingEnd, "day")
    ) {
      if (disabledDateFn(currentDate.add(1, "day"))) {
        rangeEnd = currentDate;
        break;
      }

      if (currentDate.isSame(bookingEnd, "day")) {
        rangeEnd = currentDate;
        break;
      }

      currentDate = currentDate.add(1, "day");
    }

    return { start: rangeStart, end: rangeEnd || bookingEnd };
  };

  const getDisabledDates = (record, billingData) => {
    const disabledRanges = [];

    return (current) => {
      if (!current) return false;

      const currentDate = dayjs(current).startOf("day");

      // console.log(`📅 Checking date: ${currentDate.format("DD-MM-YYYY")}`);

      // Get booking period
      const bookingStart = record.startDate
        ? dayjs(record.startDate, "YYYY-MM-DD").startOf("day")
        : null;
      const bookingEnd = record.endDate
        ? dayjs(record.endDate, "YYYY-MM-DD").endOf("day")
        : null;

      // console.log(
      //   `✅ Booking Range: ${bookingStart?.format(
      //     "DD-MM-YYYY"
      //   )} to ${bookingEnd?.format("DD-MM-YYYY")}`
      // );

      // Disable dates outside the booking range
      if (
        bookingStart &&
        bookingEnd &&
        (currentDate.isBefore(bookingStart) || currentDate.isAfter(bookingEnd))
      ) {
        // console.log(`❌ Disabled: Outside booking range`);
        return true;
      }

      // Debug: Check disabled ranges
      // console.log(
      //   "🛠️ Disabled Ranges: ",
      //   disabledRanges.map(
      //     (d) =>
      //       `${d.start.format("DD-MM-YYYY")} to ${d.end.format("DD-MM-YYYY")}`
      //   )
      // );

      return disabledRanges.some(({ start, end }) => {
        return (
          currentDate.isSame(start, "day") ||
          currentDate.isSame(end, "day") ||
          currentDate.isBetween(start, end, "day", "[]")
        );
      });
    };
  };

  const columns = [
    {
      title: "Select",
      dataIndex: "select",
      key: "select",
      align: "center",
      render: (_, record) => {
        const inventoryBilled = isInventoryBilled(record, billingData);

        return (
          <Checkbox
            checked={selectedRows[record._id] || false}
            onChange={() => handleCheckboxChange(record._id)}
            disabled={inventoryBilled} // Disable checkbox if inventory is billed
          />
        );
      },
    },
    {
      title: "Vendor",
      dataIndex: "CodeNo",
      key: "CodeNo",
      align: "center",
      fixed: "left",
      render: (_, record) => (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.4rem",
          }}
        >
          <span>{record?.isTrading ? record?.ownedBy : record?.CodeNo}</span>
          {isMagazineInventory(record) && (
            <Tooltip title="Magazine inventory — verify the applicable GST treatment">
              <Tag
                color="gold"
                className="magazine-gst-indicator"
              >
                <FaRegNewspaper />
                <span>Magazine</span>
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      render: (text) => (
        <>
          {text}
          <div style={{ width: "200px", height: "1px" }}></div>
        </>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "center",
      render: (_, record) => {
        // Use the original specialRate from the initial data that never changes
        // This comes from the BDF data and represents the full booking period amount
        const originalSpecialRate = record?.originalSpecialRate || 0;
        return <>{Math.round(originalSpecialRate)}</>;
      },
    },
    {
      title: "Due Amount",
      dataIndex: "dueAmount",
      key: "dueAmount",
      align: "center",
      render: (_, record) => {
        const raw = record?.apiDueAmount;
        if (raw === undefined || raw === null || raw === "") return <>—</>;
        return <>{Math.round(Number(raw))}</>;
      },
    },
    {
      title: "Billing Amount",
      dataIndex: "specialRate",
      key: "specialRate",
      align: "center",
      render: (_, record) => {
        const logInventory = log?.inventories?.find(
          (inv) =>
            inv._id === record._id ||
            inv._id === record.inventoryId,
        );
        const hasChanged =
          logInventory && record?.specialRate !== logInventory?.specialRate;

        const waitForDates = billingAmountNeedsDatesFirst(record);
        const billingAmountDisplay = getBillingAmountDisplay(record);
        const billingOverDue = billingAmountExceedsDueForRow(record);

        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <Input
              style={{
                width: "100px",
                backgroundColor: hasChanged ? "#fff0f0" : "transparent",
              }}
              disabled={
                billingData?.isFromSub || waitForDates
              }
              value={billingAmountDisplay}
              onChange={(e) => {
                // User override: treat Billing Amount as manual until Billing Period changes again.
                setBillingData((prev) => ({
                  ...prev,
                  inventories: prev.inventories.map((item) =>
                    item._id === record._id
                      ? { ...item, billingAmountMode: "manual" }
                      : item
                  ),
                }));
                handleInvChange(e.target.value, record._id, "specialRate");
              }}
            />
            {billingOverDue && (
              <Tooltip
                title="Billing Amount must be less than or equal to Due Amount!"
                placement="topRight"
                getPopupContainer={() => document.body}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-6px",
                    zIndex: 2,
                    color: "#fa8c16",
                    lineHeight: 1,
                    cursor: "default",
                  }}
                  aria-label="Billing amount exceeds due amount"
                >
                  <WarningOutlined style={{ fontSize: "20px" }} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          Special Rate{"\n"}Per Month
        </div>
      ),
      dataIndex: "specialRatePerMonth",
      key: "specialRatePerMonth",
      align: "center",
      render: (_, record) => {
        const logInventory = log?.inventories?.find(
          (inv) =>
            inv._id === record._id ||
            inv._id === record.inventoryId,
        );
        const hasChanged =
          logInventory &&
          record?.specialRatePerMonth !== logInventory?.specialRatePerMonth;

        return (
          <Input
            style={{
              width: "100px",
              backgroundColor: hasChanged ? "#fff0f0" : "transparent",
            }}
            value={record?.specialRatePerMonth}
            onChange={(e) =>
              handleInvChange(e.target.value, record._id, "specialRatePerMonth")
            }
          />
        );
      },
    },
    {
      title: "Booking Period",
      dataIndex: "Booking Period",
      key: "Booking Period",
      align: "center",
      render: (_, record) => {
        // Convert dates to DD-MM-YYYY format
        const rangeValue =
          record.startDate && record.endDate
            ? [
              dayjs(record.startDate, "YYYY-MM-DD"),
              dayjs(record.endDate, "YYYY-MM-DD"),
            ]
            : [];

        // Find the previous log entry for this inventory
        const logInventory = log?.inventories?.find(
          (inv) =>
            inv._id === record._id ||
            inv._id === record.inventoryId,
        );

        // Check if dates have changed
        const hasChanged =
          logInventory &&
          (logInventory.startDate !== record.startDate ||
            logInventory.endDate !== record.endDate);

        return (
          <DatePicker.RangePicker
            value={rangeValue}
            disabled={true}
            format={"DD-MM-YYYY"} // <-- Ensures the displayed format is DD-MM-YYYY
            style={{
              width: "230px",
              backgroundColor: hasChanged ? "#fff0f0" : "transparent",
            }}
          // onChange={(dates, dateStrings) => {
          //   handleDateRangeChange(dates, dateStrings, record._id);
          // }}
          />
        );
      },
    },
    {
      title: "Billing Period",
      dataIndex: "Billing Period",
      key: "Billing Period",
      align: "center",
      render: (_, record) => {
        const bookingWindowStart = record.startDate
          ? dayjs(record.startDate, "YYYY-MM-DD")
          : null;
        const bookingWindowEnd = record.endDate
          ? dayjs(record.endDate, "YYYY-MM-DD")
          : null;

        /** Same month for both panels avoids jumping to the "end" month after the first click. */
        const defaultPickerValue =
          bookingWindowStart &&
          bookingWindowEnd &&
          bookingWindowStart.isValid() &&
          bookingWindowEnd.isValid()
            ? [
                bookingWindowStart.startOf("month"),
                bookingWindowStart.startOf("month"),
              ]
            : undefined;

        let billingStart, billingEnd;

        // Check if we have matching data in archivedInvoiceData
        // if (archivedInvoiceData?.billedInvs) {
        //   const archivedInv = archivedInvoiceData.billedInvs.find(
        //     (archived) =>
        //       (archived.CodeNo &&
        //         record.CodeNo &&
        //         archived.CodeNo === record.CodeNo) ||
        //       (archived.locationName &&
        //         record.locationName &&
        //         archived.locationName === record.locationName)
        //   );

        //   if (archivedInv) {
        //     billingStart = archivedInv.startDate
        //       ? dayjs(archivedInv.startDate)
        //       : null;
        //     billingEnd = archivedInv.endDate
        //       ? dayjs(archivedInv.endDate)
        //       : null;
        //   }
        // }

        // Fall back to record's billing dates if not found in archived data
        if (!billingStart || !billingEnd) {
          const bookingStart = record.startDate
            ? dayjs(record.startDate, "YYYY-MM-DD")
            : null;
          const bookingEnd = record.endDate
            ? dayjs(record.endDate, "YYYY-MM-DD")
            : null;

          if (totalAndDueDifferForAutoPeriod(record)) {
            // Total ≠ Due: do not mirror booking period — user must pick billing period explicitly
            billingStart = record.billingStartDate
              ? dayjs(record.billingStartDate, "YYYY-MM-DD")
              : null;
            billingEnd = record.billingEndDate
              ? dayjs(record.billingEndDate, "YYYY-MM-DD")
              : null;
          } else {
            // Default to booking period if no separate billing period exists
            billingStart = record.billingStartDate
              ? dayjs(record.billingStartDate, "YYYY-MM-DD")
              : bookingStart;
            billingEnd = record.billingEndDate
              ? dayjs(record.billingEndDate, "YYYY-MM-DD")
              : bookingEnd;
          }
        }

        // Prevent "Invalid Date" error
        const rangeValue =
          billingStart &&
            billingEnd &&
            billingStart.isValid() &&
            billingEnd.isValid()
            ? [billingStart, billingEnd]
            : null;

        // Get billed periods for tooltip
        const billedPeriods = getBilledPeriods(record, billingData);
        const tooltipContent = formatBilledPeriodsTooltip(billedPeriods);

        const rangePicker = (
          <BillingPeriodRangePicker
            key={record._id}
            record={record}
            billingData={billingData}
            rangeValue={rangeValue}
            defaultPickerValue={defaultPickerValue}
            getDisabledDates={getDisabledDates}
            onRangeChange={(dates, dateStrings) =>
              handleBillingPeriodChange(dates, dateStrings, record._id)
            }
          />
        );

        return (
          <div style={{ position: "relative" }}>
            {billedPeriods.length > 0 ? (
              <Tooltip
                title={tooltipContent}
                placement="topRight"
                overlayStyle={{ maxWidth: "350px" }}
                color="#fff"
                overlayInnerStyle={{
                  color: "#000",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                mouseEnterDelay={0.15}
                getPopupContainer={() => document.body}
              >
                {/* Tooltip must wrap a DOM node; custom components + table cells break hover without this */}
                <span
                  style={{
                    display: "inline-block",
                    width: "230px",
                    verticalAlign: "middle",
                  }}
                >
                  {rangePicker}
                </span>
              </Tooltip>
            ) : (
              rangePicker
            )}
            {/* Visual indicator for billed periods */}
            {billedPeriods.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#52c41a",
                  borderRadius: "50%",
                  border: "1px solid #fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
                title={`${billedPeriods.length} previous billing period${billedPeriods.length !== 1 ? "s" : ""
                  }`}
              />
            )}
          </div>
        );
      },
    },
    // {
    //   title: (
    //     <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
    //       Campaign Duration{"\n"}(Days)
    //     </div>
    //   ),
    //   dataIndex: "Campaign Duration (Days)",
    //   key: "Campaign Duration (Days)",
    //   align: "center",
    //   render: (_, record) => {
    //     if (record.startDate && record.endDate) {
    //       const duration =
    //         dayjs(record.endDate).diff(dayjs(record.startDate), "day") + 1;
    //       return <span>{duration}</span>;
    //     }
    //     return <span>-</span>;
    //   },
    // },
    {
      title: (
        <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          Billing Duration{"\n"}(Days)
        </div>
      ),
      dataIndex: "Billing Duration (Days)",
      key: "Billing Duration (Days)",
      align: "center",
      render: (_, record) => {
        // Use billing period dates if available, otherwise fall back to booking period
        const billingStart = record.billingStartDate
          ? dayjs(record.billingStartDate, "YYYY-MM-DD")
          : record.startDate
            ? dayjs(record.startDate, "YYYY-MM-DD")
            : null;

        const billingEnd = record.billingEndDate
          ? dayjs(record.billingEndDate, "YYYY-MM-DD")
          : record.endDate
            ? dayjs(record.endDate, "YYYY-MM-DD")
            : null;

        if (billingStart && billingEnd) {
          const duration = billingEnd.diff(billingStart, "day") + 1;
          return <span>{duration}</span>;
        }
        return <span>-</span>;
      },
    },
  ];

  const dataSource = billingData?.inventories;
  // console.log("dataSource", billingData);

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  const rowClassName = (record) => {
    return isInventoryBilled(record, billingData) ? "disabled-row" : "";
  };

  return (
    <div>
      <Table
        columns={columns}
        rowKey="_id"
        dataSource={dataSource}
        components={components}
        rowClassName={rowClassName}
        scroll={{ x: "max-content" }}
        pagination={false}
      />
    </div>
  );
};

export default InventoryList;
